import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Contact,
  FileText,
  LayoutDashboard,
  type LucideIcon,
  Settings,
  Users,
} from 'lucide-react';
import type { ComponentProps } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserProfile } from '@/components/UserProfile';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { cn } from '@/lib/utils';
import { OrganizationSwitcher } from '../organization-switcher/OrganizationSwitcher';

const SIDEBAR_COLLAPSIBLE_CLASSES =
  'px-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0';

// `section: 'admin'` items render under a separate "Admin" header at the bottom
// of the nav (org/staff administration), mirroring Amy's tax mockup. Everything
// else sits in the main, unlabelled group.
type NavItem = {
  id: string;
  title: string;
  href: string;
  icon: LucideIcon;
  section?: 'admin';
  children?: ReadonlyArray<{ id: string; title: string; href: string }>;
};

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'dashboard', title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  {
    id: 'extractors',
    title: 'Document Tools',
    href: '/extractors',
    icon: FileText,
    children: [
      { id: 'payslip', title: 'Payslip Extractor', href: '/extractors/payslip' },
      { id: 'document-splitter', title: 'US Return Splitter', href: '/extractors/document-splitter' },
    ],
  },
  { id: 'clients', title: 'Clients', href: '/organization/clients', icon: Building2 },
  { id: 'contacts', title: 'Contacts', href: '/organization/contacts', icon: Contact },
  { id: 'staff', title: 'Staff', href: '/staff', icon: Users, section: 'admin' },
  { id: 'settings', title: 'Settings', href: '/organization/settings', icon: Settings, section: 'admin' },
];

type AppSidebarProps = ComponentProps<typeof Sidebar>;

export const AppSidebar = ({ className, ...props }: AppSidebarProps) => {
  const { pathname } = useLocation();
  const fbarEnabled = useFeatureFlag('tax_fbar_generator');
  const capitalGainsWorkflowEnabled = useFeatureFlag('tax_cg_workflow');
  // Org-management surface (Clients & Contacts) is hidden until the
  // shared per-org flag is switched on.
  const orgManagementEnabled = useFeatureFlag('tax_org_management');
  const withCapitalGainsWorkflow: readonly NavItem[] = capitalGainsWorkflowEnabled
    ? NAV_ITEMS.map((item) =>
        item.id === 'extractors'
          ? {
              ...item,
              children: [
                ...(item.children ?? []),
                {
                  id: 'capital-gains-workflow',
                  title: 'Capital Gains Extractor',
                  href: '/extractors/capital-gains-v2',
                },
              ],
            }
          : item
      )
    : NAV_ITEMS;
  const withFbar: readonly NavItem[] = fbarEnabled
    ? withCapitalGainsWorkflow.map((item) =>
        item.id === 'extractors'
          ? {
              ...item,
              children: [...(item.children ?? []), { id: 'fbar', title: 'FBAR Generator', href: '/extractors/fbar' }],
            }
          : item
      )
    : withCapitalGainsWorkflow;
  const navItems: readonly NavItem[] = orgManagementEnabled
    ? withFbar
    : withFbar.filter((item) => item.id !== 'clients' && item.id !== 'contacts' && item.id !== 'settings');
  const mainItems = navItems.filter((item) => item.section !== 'admin');
  const adminItems = navItems.filter((item) => item.section === 'admin');

  const renderItem = (item: NavItem) => (
    <SidebarMenuItem className="flex flex-col" key={item.id}>
      <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.title}>
        <Link to={item.href}>
          <item.icon className="size-6" />
          <span className="font-body font-medium text-sm group-data-[collapsible=icon]:hidden">{item.title}</span>
        </Link>
      </SidebarMenuButton>
      {item.children && (pathname === item.href || pathname.startsWith(`${item.href}/`)) ? (
        <SidebarMenuSub>
          {item.children.map((child) => (
            <SidebarMenuSubItem key={child.id}>
              <SidebarMenuSubButton asChild isActive={pathname === child.href}>
                <Link to={child.href}>{child.title}</Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );

  return (
    <Sidebar collapsible="icon" {...props} className={cn('bg-sidebar', className)}>
      <SidebarTrigger className="-right-3 absolute top-12 z-10 size-6 rounded-full border border-border bg-background shadow-md hover:bg-sidebar-accent">
        <ChevronRight className="size-4 group-data-[state=expanded]:hidden" />
        <ChevronLeft className="size-4 group-data-[state=collapsed]:hidden" />
      </SidebarTrigger>

      <SidebarContent className="overflow-x-hidden">
        {/* Logo */}
        <div className="flex shrink-0 items-center px-4 pt-6 pb-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <h1 className="font-heading font-semibold text-2xl text-foreground">
            <span className="group-data-[collapsible=icon]:hidden">Tax Pod</span>
            <span className="hidden group-data-[collapsible=icon]:inline">T</span>
          </h1>
        </div>

        {/* Organisation Switcher */}
        <SidebarMenu className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'pt-2')}>
          <OrganizationSwitcher />
        </SidebarMenu>

        <SidebarSeparator />

        {/* Navigation — flex-1 pushes content to top within SidebarContent */}
        <div className="flex flex-1 flex-col">
          <SidebarMenu className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'pt-2')}>{mainItems.map(renderItem)}</SidebarMenu>

          {adminItems.length > 0 && (
            <>
              <SidebarGroupLabel className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'mt-4')}>Admin</SidebarGroupLabel>
              <SidebarMenu className={SIDEBAR_COLLAPSIBLE_CLASSES}>{adminItems.map(renderItem)}</SidebarMenu>
            </>
          )}
        </div>
      </SidebarContent>

      <SidebarFooter className={cn(SIDEBAR_COLLAPSIBLE_CLASSES, 'py-4')}>
        <UserProfile />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
